<template>
    <v-main class="tenant-register">
        <div class="content">
            <div
                v-if="!$vuetify.breakpoint.smAndDown"
                class="bg-graphics"
                :style="bgStyle"
            ></div>

            <select
                v-if="isMobile"
                class="country"
                @change="onCountryChange"
            >
                <option value="" disabled :selected="!['me', 'sr', 'ba'].includes(locale)">{{ $t('general.country') }}</option>
                <option value="sr" :selected="locale === 'sr'">{{ $t('locales.sr') }}</option>
                <option value="me" :selected="locale === 'me'">{{ $t('locales.me') }}</option>
                <option value="ba" :selected="locale === 'ba'">{{ $t('locales.ba') }}</option>
            </select>

            <v-form ref="form" class="form">
                <div class="logo">
                    <img v-if="isAllegra" src="../../assets/img/allegra/logoDarkHor.svg" alt="" />
                    <img v-else src="../../assets/img/aria/logoDarkVer.svg" alt="" />
                </div>

                <div class="form-wrapper">
                    <h3 class="mb-1">{{ $t('general.register') }}</h3>

                    <p class="text-subtitle-2 grey--text text--darken-2 mb-5">
                        {{ $t('get_started.register_intro') }}
                    </p>

                    <v-text-field
                        ref="nameField"
                        v-model="form.name"
                        :label="$t('general.owner_name')"
                        :error-messages="form.$errors.name"
                        autofocus
                        validate-on-blur
                        class="mt-5"
                        outlined
                        dark
                        @blur="form.validate('name')"
                        @input="delete form.$errors.name"
                    />

                    <v-text-field
                        v-model="form.email"
                        :label="$t('general.email')"
                        :error-messages="form.$errors.email"
                        validate-on-blur
                        outlined
                        dark
                        @blur="form.validate('email')"
                        @input="delete form.$errors.email"
                    />

                    <v-text-field
                        v-model="form.phone"
                        v-mask="'+############'"
                        :label="$t('general.phone')"
                        :error-messages="form.$errors.phone"
                        validate-on-blur
                        outlined
                        dark
                        @blur="form.validate('phone')"
                        @input="delete form.$errors.phone"
                    />

                    <v-text-field
                        v-model="form.password"
                        :append-icon="showPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showPassword ? 'text' : 'password'"
                        :label="$t('general.password')"
                        :error-messages="form.$errors.password"
                        counter
                        outlined
                        dark
                        @click:append="showPassword = !showPassword"
                        @input="$refs.repeatPasswordField.validate(); delete form.$errors.password"
                        @blur="form.validate('password')"
                    />

                    <v-text-field
                        ref="repeatPasswordField"
                        v-model="form.password_confirmation"
                        :label="$t('general.password_confirmation')"
                        :error-messages="form.$errors.password_confirmation"
                        :type="showPassword ? 'text' : 'password'"
                        outlined
                        dark
                        @blur="form.validate('password_confirmation')"
                        @input="delete form.$errors.password_confirmation"
                    />

                    <v-autocomplete
                        v-model="form.reseller_id"
                        :search-input.sync="resellerSearch"
                        :items="resellers"
                        item-value="id"
                        item-text="name"
                        :error-messages="form.$errors.reseller_id"
                        :label="$tc('general.reseller', 1)"
                        clear-icon="fal fa-times"
                        clearable
                        outlined
                        auto-select-first
                        dark
                        @blur="form.validate('reseller_id')"
                        @input="delete form.$errors.reseller_id"
                    />

                    <v-text-field
                        v-if="locale === 'sr' || locale === 'ba'"
                        v-model="form.reseller_hash"
                        :append-icon="showResellerHash ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showResellerHash ? 'text' : 'password'"
                        dark
                        outlined
                        :error-messages="form.$errors.reseller_hash"
                        :label="$t('general.reseller_code')"
                        @click:append="showResellerHash = !showResellerHash"
                        @blur="form.validate('reseller_hash')"
                        @input="delete form.$errors.reseller_hash"
                    />

                    <v-text-field
                        v-if="showPromoCode"
                        v-model="form.promo_code"
                        :label="$t('general.promo_code')"
                        :error-messages="form.$errors.promo_code"
                        validate-on-blur
                        outlined
                        dark
                        @blur="form.validate('promo_code')"
                        @input="delete form.$errors.promo_code"
                    />

                    <v-btn
                        :disabled="form.$busy"
                        :loading="form.$busy"
                        color="primary"
                        block
                        depressed
                        @click="register()"
                    >
                        {{ $t('general.register') }}
                    </v-btn>

                    <div class="d-flex mt-5">
                        <a
                            v-if="!disablePromoCode"
                            href="#"
                            @click.prevent="showPromoCode = !showPromoCode"
                        >
                            {{ $t('general.promo_code') }}?
                        </a>

                        <v-spacer />

                        <a href="#" @click.prevent="$router.push({ name: 'tenantLogin' }).catch(err => {})">
                            {{ $t('general.already_have_an_account') }}
                        </a>
                    </div>
                </div>
            </v-form>
        </div>
    </v-main>
</template>

<script>
// Vuex Store
import TenantService from '@/services/TenantService';
import Locale from '@/wrappers/Locale';
import { required, requiredIf, email, minLength, maxLength, sameAs, helpers } from 'vuelidate/lib/validators';

export default {
    async created() {
        const subscription = this.$route.params.subscription
        if(subscription === 'free') {
            this.form.promo_code = 'FREE'
            this.disablePromoCode = true
        }

        const response = await ApiService.get('/register/load', { params: { locale } })
        this.allReselers = response.data.resellers

        this.isMobile = this.device.platform === 'android' || this.device.platform === 'ios'
    },

    data() {
        return {
            form: new Form(this, 'form', {
                name: null,
                email: null,
                phone: null,
                password: null,
                password_confirmation: null,
                reseller_id: null,
                reseller_hash: null,
                promo_code: null,
                app: this.isAllegra ? 'allegra' : 'aria'
            }),

            showPassword: false,
            showResellerHash: false,
            showPromoCode: false,
            disablePromoCode: false,

            allReselers: [],
            resellerSearch: null,
            isMobile: false
        };
    },

    validations() {
        return {
            form: {
                name: { required, maxLength: maxLength(40) },
                email: { required, email },
                password: { required, minLength: minLength(8) },
                password_confirmation: { required, minLength: minLength(8), sameAs: sameAs('password') },
                reseller_id: { requiredIf: requiredIf(form => locale === 'sr') },
                reseller_hash: {
                    requiredIf: requiredIf(form => locale === 'sr' || locale === 'ba'),
                    matchHash: helpers.withParams(
                        { type: 'matchHash' },
                        function(value) {
                            if(!value) return true
                            return this.allReselers.find(r => r.id === this.form.reseller_id)?.hash === value
                        }
                    )
                },
            }
        }
    },

    methods: {
        register() {
            if (!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error', { timeout: 10000 });
                return;
            }

            if (!this.form.validate()) return;

            TenantService.register(this.form).then(response => {
                this.$router.push({ name: 'gettingStarted' }).catch(err => {});
            });
        },

        async onCountryChange(event) {
            await Locale.setLocale(event.target.value)
            Locale.setLanguage(event.target.value)
        }
    },

    computed: {
        bgStyle() {
            return { backgroundImage: `url('/img/${this.isAllegra ? 'allegra' : 'aria'}/background.png')` }
        },

        resellers() {
            return this.allReselers.filter(reseller => {
                return this.resellerSearch
                    && this.resellerSearch.length >= 5
                    && reseller.name.toLowerCase().includes(this.resellerSearch.toLowerCase())
            })
        }
    }
};
</script>

<style lang="scss" scoped>
.tenant-register {
    .content {
        height: 100%;
        display: flex;
        justify-content: center;
        background: #1f212b;
        color: white;
        position: relative;

        .country {
            position: absolute;
            top: 12px;
            right: 36px;
            color: #bcbdc0;
            text-align: right;
            outline: none;
            cursor: pointer;
        }

        .form {
            display: flex;
            flex-direction: column;
            padding: 44px 36px;
            flex: 3;
            max-width: 600px;
            justify-content: center;

            .logo {
                display: flex;
                justify-content: center;
                width: 100%;
                max-height: 120px;
                margin-top: 20px;
                margin-bottom: 20px;

                img {
                    max-height: 100%;
                    max-width: 250px;
                }
            }

            .form-wrapper {
                display: flex;
                flex-direction: column;
            }
        }

        .bg-graphics {
            flex: 7;
            background-size: cover;
            background-position: bottom left;
            background-color: #101219;
        }
    }
}
</style>
